/**
 * @prop --topnews-background-color: Background color of top news section
 * @prop --mostsearched-background-color: Background color of top most searched section
 *
 * @prop --stzh-homepage-service-highlights-topnews-subtitle-font: **Global**: Font of the topnews subtitle
 * @prop --stzh-homepage-service-highlights-topnews-item-subtitle-color: **Global**: Color of the topnews subtitle
 * @prop --stzh-homepage-service-highlights-topnews-item-subtitle-highlighted-color: **Global**: Color of the topnews subtitle (highlighted variant)
 */

:host {
  --mostsearched-background-color: var(
    --stzh-homepage-service-highlights-mostsearched-background-color,
    #{$mostsearchedBackgroundColor}
  );
  --mostsearched-item-font-size: var(
    --stzh-homepage-service-highlights-mostsearched-item-font-size,
    "inherit"
  );
  --mostsearched-color: var(
    --stzh-homepage-service-highlights-mostsearched-color,
    #{$linkColor}
  );
  --topnews-background-color: var(
    --stzh-homepage-service-highlights-topnews-background-color,
    #{$topnewsBackgroundColor}
  );
  --topnews-background-color-hover: var(
    --stzh-homepage-service-highlights-topnews-background-color-hover,
    #{$colorSecondary40}
  );
  --topnews-color-hover: var(
    --stzh-homepage-service-highlights-topnews-hover,
    #{$colorPrimary70}
  );
  --topnews-item-subtitle-color: var(
    --stzh-homepage-service-highlights-topnews-item-subtitle-color,
    #{$colorPrimary70}
  );
  --topnews-item-subtitle-font: var(
    --stzh-homepage-service-highlights-topnews-subtitle-font,
    #{$topnewsSubtitleFont}
  );
  --topnews-item-subtitle-highlighted-color: var(
    --stzh-homepage-service-highlights-topnews-item-subtitle-highlighted-color,
    #{$colorPrimary60}
  );
}

.stzh-homepage-service-highlights {
  display: grid;

  &__topnews,
  &__mostsearched {
    display: flex;
    flex-direction: column;
  }

  &--has-topnews {
    @include mq($from: medium) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  /**
   * Top news.
   */

  .stzh-topnews-item {
    @include spaceCurve("padding-top", "regular");
    @include spaceCurve("padding-bottom", "regular");
    padding-left: space("large");
    padding-right: space("large");
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;

    @include mq($from: small) {
      padding-left: $containerMarginSmall;
      padding-right: $containerMarginSmall;
    }

    @include mq($from: medium) {
      padding-left: $containerMarginMedium;
      padding-right: $containerMarginMedium;
    }

    @include mq($from: large) {
      padding-left: $containerMarginLarge;
      padding-right: $containerMarginLarge;
    }

    @include mq($from: ultra) {
      padding-left: calc(
        ((100vw - #{$containerMaxWidth} - var(--stzh-scrollbar-width, 0px)) / 2)
      );
    }
  }

  .stzh-topnews-item--has-link {
    transition: background-color 150ms ease;
    text-decoration: none;

    &:hover {
      background-color: var(--topnews-background-color-hover);
    }
  }

  .stzh-topnews-item--highlighted {
    background-color: var(--stzh-color-lemonyellow50);
  }

  .stzh-topnews-item--highlighted.stzh-topnews-item--has-link {
    &:hover {
      background-color: var(--stzh-color-lemonyellow60);
    }
  }

  .stzh-topnews-item__subtitle {
    --color: var(--topnews-item-subtitle-color);
    font-family: var(--topnews-item-subtitle-font);
  }

  .stzh-topnews-item--highlighted .stzh-topnews-item__subtitle {
    --color: var(--topnews-item-subtitle-highlighted-color);
  }

  .stzh-topnews-item__label {
    //@include fontCurve('h4', 'heading'); // default
  }

  .stzh-topnews-item.stzh-topnews-item--has-link:not(
      .stzh-topnews-item--highlighted
    ):hover
    .stzh-topnews-item__label {
    --color: var(--stzh-color-primary80);
  }

  .stzh-topnews-item--highlighted:hover .stzh-topnews-item__subtitle {
    --color: var(--topnews-color-hover);
  }

  &__topnews {
    background-color: var(--topnews-background-color);
  }

  /**
   * Most searched.
   */
  &__mostsearched {
    background-color: var(--mostsearched-background-color);
    @include spaceCurve("padding-top", "regular");
    padding-right: space("large");
    @include spaceCurve("padding-bottom", "regular");
    padding-left: space("large");

    @include mq($from: small) {
      padding-left: $containerMarginSmall;
      padding-right: $containerMarginSmall;
    }

    @include mq($from: medium) {
      padding-left: $containerMarginMedium;
      padding-right: calc(
        ((100vw - #{$containerMaxWidth} - var(--stzh-scrollbar-width, 0px)) / 2)
      );
    }
  }

  &:not(&--has-topnews) &__mostsearched {
    @include mq($from: medium) {
      display: grid;
      gap: 0 space("large");
      grid-template-columns: repeat(2, minmax(0, 1fr));
      padding-left: $containerMarginMedium;
      padding-right: $containerMarginMedium;
    }

    @include mq($from: large) {
      padding-left: $containerMarginLarge;
      padding-right: $containerMarginLarge;
    }

    @include mq($from: ultra) {
      padding-left: calc(
        ((100vw - #{$containerMaxWidth} - var(--stzh-scrollbar-width, 0px)) / 2)
      );
      padding-right: calc(
        ((100vw - #{$containerMaxWidth} - var(--stzh-scrollbar-width, 0px)) / 2)
      );
    }
  }

  .stzh-mostsearched-item {
    @include spaceCurve("padding-top", "tiny");
    @include spaceCurve("padding-bottom", "tiny");
  }

  .stzh-mostsearched-item__link {
    --color: var(--mostsearched-color);
    --text-decoration-line: none;
    --hover-text-decoration-line: none;
    --hover-color: $linkColor;

    @include mq($from: medium) {
      --font-size: var(--mostsearched-item-font-size);
    }

    transition: transform 150ms ease;

    &:hover {
      transform: translateX(10px);

      @include mq($from: medium) {
        transform: translateX(20px);
      }
    }
  }
}
